import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'

import * as api from '../../lib/api'
import { t } from 'stores/i18n.store'

const tagBase = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 10px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  textDecoration: 'none'
}

const starIconBase = {
  width: 16,
  height: 16,
  color: '#d1d5db',
  display: 'block'
}

const StarRating = ({ value, max }) => {
  return (
    <div style={styles.starGroup}>
      {Array.from({ length: max }, (_, i) => (
        <svg key={i} style={i < value ? styles.starIconActive : styles.starIcon} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  )
}

// Index { _id: valeur } d'une liste d'éléments, pour étiqueter chaque avis.
const indexById = (list, pick) => {
  const index = {}
  ;(list || []).forEach(item => {
    index[item._id] = pick(item)
  })
  return index
}

const experienceLabels = experiences =>
  indexById(experiences, experience => ({ name: experience.name, siteId: experience.siteId }))

export default function UserRatings({ siteId, experienceId, experienceIds, experiences, from, to, excludeZeros }) {
  const [comments, setComments] = useState(null)
  const [experiencesById, setExperiencesById] = useState({})
  const [siteNames, setSiteNames] = useState({})

  // Le tableau reçu en prop change d'identité à chaque render du parent : seul son contenu doit relancer la requête.
  const experienceIdsKey = (experienceIds || []).filter(Boolean).join(',')
  const requestedExperienceIds = useMemo(() => (experienceIdsKey ? experienceIdsKey.split(',') : []), [experienceIdsKey])

  useEffect(() => {
    let cancelled = false
    api.sessionsEvents
      .stats('locales', {
        from: from || new Date(Date.now() - 365 * 24 * 3600 * 1000).toISOString(),
        to,
        limit: 100000,
        siteId,
        experienceId,
        experienceIds: requestedExperienceIds,
        excludeZeros,
        axisProperties: ['locale']
      })
      .then(events => {
        if (!cancelled) setComments(events)
      })
    return () => {
      cancelled = true
    }
  }, [siteId, experienceId, requestedExperienceIds, from, to, excludeZeros])

  // Les avis ne portent qu'un experienceId : les noms viennent de la liste du site quand le parent la détient,
  // sinon d'un appel dédié — seul cas disponible aux niveaux « une expérience » et « toutes les expériences ».
  useEffect(() => {
    if (experiences && experiences.length > 0) {
      setExperiencesById(experienceLabels(experiences))
      return
    }
    let cancelled = false
    api.experiences.listActiveLite().then(list => {
      if (!cancelled) setExperiencesById(experienceLabels(list))
    })
    return () => {
      cancelled = true
    }
  }, [experiences])

  // L'expérience porte le siteId, mais aucun appel ne donne le nom du site : il vient de son propre index.
  useEffect(() => {
    let cancelled = false
    api.sites.listActiveLite().then(list => {
      if (!cancelled) setSiteNames(indexById(list, site => site.name))
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (!comments || comments.length === 0) {
    return (
      <div style={styles.emptyPanel}>
        <div style={styles.emptyContent}>
          <div style={styles.emptyIcon}>
            <svg style={styles.emptyIconSvg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h3 style={styles.emptyTitle}>{t('stats-userRatings-empty-title')}</h3>
          <p style={styles.emptyDescription}>{t('stats-userRatings-empty-description')}</p>
        </div>
      </div>
    )
  }

  // Trier les avis par date (plus récent en premier)
  const sortedComments = [...comments].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  return (
    <div style={styles.panel}>
      <div style={styles.panelHeader}>
        <h3 style={styles.title}>{t('stats-userRatings-title')}</h3>
      </div>

      <div style={styles.panelBody}>
        <div style={styles.list}>
          {sortedComments.map(userComment => {
            const experience = experiencesById[userComment.experienceId]
            const siteName = experience && siteNames[experience.siteId]
            return (
              <div key={userComment._id} style={styles.card}>
                {/* Header avec avatar et infos */}
                <div style={styles.cardTop}>
                  <div style={styles.cardRow}>
                    <div style={styles.avatar}>
                      <svg style={styles.avatarIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                    </div>
                    <div style={styles.cardBody}>
                      {experience && (
                        <div style={styles.cardTitleRow}>
                          {siteName && (
                            <Link to={`/site/${experience.siteId}`} style={styles.siteTag}>
                              {siteName}
                            </Link>
                          )}
                          {experience.siteId ? (
                            <Link to={`/site/${experience.siteId}/experience/${userComment.experienceId}`} style={styles.tag}>
                              {experience.name}
                            </Link>
                          ) : (
                            <span style={styles.tag}>{experience.name}</span>
                          )}
                        </div>
                      )}
                      <div style={styles.session}>
                        <span style={styles.sessionLabel}>{t('stats-userRatings-session')}</span>
                        <a
                          href={`https://scores.explor.games/session/${userComment.sessionId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.sessionLink}
                        >
                          {userComment.sessionId}
                        </a>
                      </div>
                      {/* Affichage des notes si disponibles */}
                      {userComment.rating && (
                        <div style={styles.ratingRow}>
                          {userComment.rating.fun !== undefined && (
                            <div style={styles.ratingItem}>
                              <span style={styles.ratingLabel}>{t('stats-userRatings-fun')}</span>
                              <StarRating value={userComment.rating.fun} max={5} />
                            </div>
                          )}
                          {userComment.rating.difficulty !== undefined && (
                            <div style={styles.ratingItem}>
                              <span style={styles.ratingLabel}>{t('stats-userRatings-difficulty')}</span>
                              <StarRating value={userComment.rating.difficulty} max={5} />
                            </div>
                          )}
                          {userComment.rating.next !== undefined && (
                            <div style={styles.ratingItem}>
                              <span style={styles.ratingLabel}>{t('stats-userRatings-replay')}</span>
                              <StarRating value={userComment.rating.next} max={5} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div style={styles.meta}>
                      <div style={styles.metaContent}>
                        <svg style={styles.metaIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <div style={styles.metaValue}>{new Date(userComment.createdAt).toLocaleString()}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contenu du commentaire */}
                {userComment.comment && (
                  <div style={styles.commentSection}>
                    <div style={styles.commentBody}>
                      <blockquote style={styles.commentText}>{userComment.comment}</blockquote>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const styles = {
  panel: {
    backgroundColor: '#ffffff',
    border: '1px solid #d9dde7',
    borderRadius: 12,
    boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)'
  },
  panelHeader: {
    padding: '18px 26px',
    borderBottom: '1px solid #e3e7ef'
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 600,
    color: '#1f2937'
  },
  subtitle: {
    margin: '6px 0 0 0',
    fontSize: 13,
    color: '#6b7280'
  },
  panelBody: {
    padding: 26
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18
  },
  card: {
    backgroundColor: '#ffffff',
    border: '1px solid #dfe4ee',
    borderRadius: 14,
    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.06)',
    overflow: 'hidden',
    transition: 'box-shadow 120ms ease, transform 120ms ease'
  },
  cardTop: {
    padding: '18px 22px',
    borderBottom: '1px solid #edf0f7'
  },
  cardRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 18
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)',
    boxShadow: '0 6px 12px rgba(37, 99, 235, 0.22)',
    flexShrink: 0
  },
  avatarIcon: {
    width: 22,
    height: 22,
    strokeWidth: 1.8,
    color: '#ffffff'
  },
  cardBody: {
    flex: 1,
    minWidth: 0
  },
  cardTitleRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8
  },
  author: {
    margin: 0,
    fontSize: 15,
    fontWeight: 600,
    color: '#111827'
  },
  tag: {
    ...tagBase,
    background: 'rgba(37, 99, 235, 0.12)',
    color: '#1d4ed8'
  },
  siteTag: {
    ...tagBase,
    background: 'rgba(15, 23, 42, 0.08)',
    color: '#334155'
  },
  session: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#6b7280',
    marginTop: 6,
    flexWrap: 'wrap'
  },
  sessionLabel: {
    fontWeight: 600,
    fontSize: 15,
    color: '#4b5563'
  },
  sessionLink: {
    fontWeight: 600,
    fontSize: 15,
    color: '#1d4ed8',
    textDecoration: 'none'
  },
  ratingRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px 24px',
    marginTop: 12
  },
  ratingItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  ratingLabel: {
    fontSize: 12,
    color: '#4b5563',
    fontWeight: 500
  },
  starGroup: {
    display: 'inline-flex',
    gap: 4,
    alignItems: 'center'
  },
  starIcon: starIconBase,
  starIconActive: { ...starIconBase, color: '#fbbf24' },
  meta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    color: '#4b5563',
    fontSize: 12,
    minWidth: 120
  },
  metaContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  metaIcon: {
    width: 18,
    height: 18,
    color: '#4b5563'
  },
  metaValue: {
    fontSize: 12,
    color: '#4b5563',
    textAlign: 'right'
  },
  commentSection: {
    padding: '20px 26px 24px 16px',
    position: 'relative'
  },
  commentAccent: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    borderRadius: 999,
    background: 'linear-gradient(180deg, #2563eb 0%, #38bdf8 100%)'
  },
  commentBody: {
    color: '#374151'
  },
  commentText: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.6,
    textAlign: 'left'
  },
  emptyPanel: {
    backgroundColor: '#ffffff',
    border: '1px solid #d9dde7',
    borderRadius: 12,
    boxShadow: '0 8px 22px rgba(15, 23, 42, 0.08)'
  },
  emptyContent: {
    textAlign: 'center',
    padding: '40px 16px',
    color: '#6b7280'
  },
  emptyIcon: {
    width: 64,
    height: 64,
    margin: '0 auto 18px',
    borderRadius: '50%',
    background: '#f3f4f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  emptyIconSvg: {
    width: 30,
    height: 30,
    color: '#9ca3af'
  },
  emptyTitle: {
    margin: '0 0 8px',
    fontSize: 17,
    fontWeight: 600,
    color: '#1f2937'
  },
  emptyDescription: {
    margin: 0,
    fontSize: 13,
    color: '#6b7280'
  }
}
